import graphql__da3f60decd2ceead803b from "./__generated__/ResponsiveImage_image.graphql.js";import { useRef, useEffect, useState, useCallback } from "react";
import { graphql, useFragment } from "react-relay";
import omit from "lodash/omit";

import dispatchEvent from "./dispatchEvent";

const ResponsiveImage = props => {
  const imageRef = useRef();

  const { image, onLoad, containerWidth } = props;
  const {
    url,
    x15Url,
    x2Url,
    altText,
    width: originalWidth,
    height: originalHeight
  } = image;

  const commonProps = omit(props, [
    "image",
    "onLoad",
    "relay",
    "width",
    "height",
    "style",
    "containerWidth"
  ]);

  const isGreaterThanContainer = containerWidth && containerWidth < image.width;

  const srcSet = `${url} 1x, ${x15Url} 1.5x, ${x2Url} 2x`;

  // Calculate the final image width and height depending on whether it needs to be
  // adapted to the provided width/height props or to the parent container width
  const width = props.width || isGreaterThanContainer ? containerWidth : originalWidth;
  const heightRatio = containerWidth / originalWidth;
  const height =
    props.height ||
    (isGreaterThanContainer ? originalHeight * heightRatio : originalHeight);

  // When the image is actually loaded from the server, refresh its container width and
  // trigger a newcontentloaded event to allow its parent to resize if needed.
  const handleImageLoaded = useCallback(() => {
    dispatchEvent(window, "newContentLoaded");

    if (imageRef.current) {
      dispatchEvent(imageRef.current, "resize");
      onLoad && onLoad(imageRef.current);
    }
  }, [onLoad]);

  const style = { width, height, ...props.style };

  return (
    <img
      ref={imageRef}
      src={x2Url}
      srcSet={srcSet}
      style={style}
      alt={altText}
      onLoad={handleImageLoaded}
      {...commonProps}
    />
  );
};

const ResponsiveImageContainer = props => {
  const { altText, large, medium, small } = useFragment(
    ResponsiveImage_image,
    props.image
  );

  // Get the image ratio (all sizes have the same ratio) to be able to calculate the
  // height of the image before it's loaded and avoid a flash of content when the image
  // is loaded.
  const { ratio: aspectRatio } = large;

  const [container, setContainer] = useState(null);
  const [imageVariant, setImageVariant] = useState(null);

  const setImageVariantFromContainerSize = useCallback(() => {
    if (!container) return;

    const { clientWidth: width } = container;

    let variant;

    if (width <= small.width) {
      variant = small;
    } else if (width <= medium.width) {
      variant = medium;
    } else {
      variant = large;
    }

    setImageVariant(variant);
  }, [container, large, medium, small]);

  useEffect(() => {
    // Only initialize this when the container is available avoiding useless event
    // listeners binding on component mount.
    if (!container) return;

    window.addEventListener("resize", setImageVariantFromContainerSize);

    setImageVariantFromContainerSize();

    return () => {
      window.removeEventListener("resize", setImageVariantFromContainerSize);
    };
  }, [setImageVariantFromContainerSize, container]);

  const containerWidth = container ? container.clientWidth : null;

  // Note that for the first render, the imageVariant will be null and the image won't be
  // rendered until the container is available and its width is known, but we use the
  // `aspectRatio` to set the height of the container and avoid a flash of content when
  // the image is loaded.
  return (
    <div className="w-100" style={{ aspectRatio }} ref={setContainer}>
      {imageVariant && (
        <ResponsiveImage
          {...props}
          image={{ ...imageVariant, altText }}
          containerWidth={containerWidth}
        />
      )}
    </div>
  );
};

const ResponsiveImage_image = graphql__da3f60decd2ceead803b;

export default ResponsiveImageContainer;
